import { Fragment } from 'react';
import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, NewsArticleSchema } from '@/components/seo/seo';
import {
  type PressRelease,
  ABOUT_FIXTYER_BOILERPLATE,
  DATA_ATTRIBUTION_LINE,
  OGL_NOTE,
  NOT_LEGAL_ADVICE_LINE,
} from '@/data/press-releases';
import { MediaContact } from './media-contact';
import { PressDownloadButton } from './press-download-button';

const INLINE_LINK_CLASS =
  'font-medium text-brand-600 hover:text-brand-700 underline decoration-brand-300 underline-offset-2';

// Inline markup: **bold** and [label](url). Internal links (starting with "/")
// use the SPA router; external links open in a new tab. First-party content
// only, so a small tokeniser is sufficient and avoids a markdown dependency.
const INLINE_RE = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;

function renderInline(text: string): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;

  while ((match = INLINE_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<Fragment key={key++}>{text.slice(lastIndex, match.index)}</Fragment>);
    }

    if (match[1] !== undefined) {
      const label = match[1];
      const href = match[2] ?? '';
      if (href.startsWith('/')) {
        nodes.push(
          <Link key={key++} to={href} className={INLINE_LINK_CLASS}>
            {label}
          </Link>
        );
      } else {
        nodes.push(
          <a
            key={key++}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={INLINE_LINK_CLASS}
          >
            {label}
          </a>
        );
      }
    } else if (match[3] !== undefined) {
      nodes.push(
        <strong key={key++} className="font-semibold text-brand-950">
          {match[3]}
        </strong>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(<Fragment key={key++}>{text.slice(lastIndex)}</Fragment>);
  }

  return nodes;
}

function PressBody({ blocks }: { blocks: PressRelease['body'] }) {
  return (
    <>
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'heading': {
            if (block.level === 3) {
              return (
                <h3 key={index} className="font-display text-xl font-bold text-brand-950 mt-8 mb-3">
                  {renderInline(block.text)}
                </h3>
              );
            }
            return (
              <h2 key={index} className="font-display text-2xl font-bold text-brand-950 mt-10 mb-4">
                {renderInline(block.text)}
              </h2>
            );
          }
          case 'paragraph':
            return (
              <p key={index} className="text-[1.0625rem] leading-relaxed text-surface-800 mb-5">
                {renderInline(block.text)}
              </p>
            );
          case 'quote':
            return (
              <blockquote
                key={index}
                className="my-8 rounded-r-2xl border-l-4 border-accent-500 bg-accent-50 p-5 sm:p-6"
              >
                <p className="text-lg font-medium leading-relaxed text-brand-950">
                  {renderInline(block.text)}
                </p>
                {block.cite && (
                  <cite className="mt-3 block text-sm not-italic text-surface-600">
                    {renderInline(block.cite)}
                  </cite>
                )}
              </blockquote>
            );
          case 'list': {
            const ListTag = block.ordered ? 'ol' : 'ul';
            return (
              <ListTag
                key={index}
                className={`mb-6 space-y-2 pl-6 text-[1.0625rem] leading-relaxed text-surface-800 ${
                  block.ordered ? 'list-decimal' : 'list-disc'
                }`}
              >
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="pl-1 marker:text-brand-400">
                    {renderInline(item)}
                  </li>
                ))}
              </ListTag>
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
}

function formatDisplayDate(date: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    // Date-only strings parse as UTC midnight; format in UTC so the
    // prerendered HTML matches the client in every timezone (hydration).
    timeZone: 'UTC',
  }).format(new Date(date));
}

function buildExpertSentence(release: PressRelease): string {
  const name = release.expertName ?? '';
  const credential = release.expertCredential;
  const worksFor = release.expertWorksFor ?? 'Your Local Mechanic';
  if (credential) {
    return `${name} is a ${credential} at ${worksFor}.`;
  }
  return `${name} is a qualified mechanic at ${worksFor}.`;
}

export function PressReleaseLayout({ release }: { release: PressRelease }) {
  const {
    title,
    slug,
    date,
    dateModified,
    displayDate,
    standfirst,
    locationDateline,
    hasNamedExpert,
    heroImage,
    heroImageAlt,
    ogImage,
    body,
  } = release;

  const url = `/press/${slug}`;
  const modified = dateModified ?? date;

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Press Releases', url: '/press' },
    { name: title, url },
  ];

  const reviewedBy =
    hasNamedExpert && release.expertName
      ? {
          name: release.expertName,
          jobTitle: release.expertJobTitle ?? 'Qualified Mechanic',
          url: release.expertUrl,
          worksFor: { name: release.expertWorksFor ?? 'Your Local Mechanic' },
          credentialCategory: release.expertCredential,
        }
      : undefined;

  return (
    <div className="relative">
      <SEO
        title={`${title} | Fixtyer Press`}
        description={standfirst}
        canonical={url}
        type="article"
        image={ogImage ?? heroImage}
        imageAlt={heroImageAlt}
        article={{
          publishedTime: date,
          modifiedTime: modified,
          author: 'Fixtyer',
        }}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <NewsArticleSchema
        title={title}
        description={standfirst}
        url={url}
        image={ogImage ?? heroImage}
        imageAlt={heroImageAlt}
        datePublished={date}
        dateModified={modified}
        reviewedBy={reviewedBy}
      />

      {/* Breadcrumb bar */}
      <div className="bg-white border-b border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-brand-600">
            <Link to="/press" className="hover:text-brand-700 transition-colors">
              Press Releases
            </Link>
            <svg className="w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-600 font-medium line-clamp-1">{title}</span>
          </div>
        </div>
      </div>

      <article>
        <header className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-12">
          <div
            className="absolute top-0 left-0 right-0 h-8"
            style={{
              background: `repeating-linear-gradient(
                -45deg,
                #f97316,
                #f97316 40px,
                #1e40af 40px,
                #1e40af 80px
              )`,
            }}
          />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-brand-700 text-sm font-medium">
                {displayDate}
              </span>
              {locationDateline && (
                <span className="text-sm font-medium text-surface-600">{locationDateline}</span>
              )}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-950 leading-tight mb-6">
              {title}
            </h1>
            <p data-speakable="standfirst" className="text-lg sm:text-xl text-surface-800 leading-relaxed max-w-2xl">
              {renderInline(standfirst)}
            </p>
            {heroImage && (
              <figure className="mt-8 overflow-hidden rounded-3xl border border-surface-200 bg-white shadow-xl shadow-brand-900/10">
                <img
                  src={heroImage}
                  alt={heroImageAlt ?? ''}
                  width={1200}
                  height={630}
                  loading="eager"
                  decoding="async"
                  className="w-full h-auto object-cover"
                />
              </figure>
            )}
          </div>
        </header>

        <section className="bg-white border-b border-surface-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-surface-600">
              <span>
                Published{' '}
                <time dateTime={date}>{formatDisplayDate(date)}</time>
              </span>
              {modified !== date && (
                <span>
                  Updated <time dateTime={modified}>{formatDisplayDate(modified)}</time>
                </span>
              )}
              <span>For immediate release</span>
            </div>
            <div className="mt-5">
              <PressDownloadButton slug={slug} />
            </div>
          </div>
        </section>

        <section className="py-10 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <PressBody blocks={body} />

            {/* ENDS marker, visually de-emphasised */}
            <p className="my-10 text-center text-sm font-semibold uppercase tracking-[0.3em] text-surface-400">
              Ends
            </p>

            {/* Notes to editors.
                Copy constraints (content, not code): no legal advice; no comment
                on individual claim outcomes; Highways Act 1980 / Section 58
                references kept general. */}
            <section className="rounded-2xl border border-surface-200 bg-surface-50 p-6 sm:p-8">
              <h2 className="font-display text-lg font-bold text-brand-950">Notes to editors</h2>
              <div className="mt-4 space-y-5 text-sm leading-relaxed text-surface-700">
                <div>
                  <p className="font-semibold text-brand-950">About Fixtyer</p>
                  <p className="mt-1">{ABOUT_FIXTYER_BOILERPLATE}</p>
                </div>

                {hasNamedExpert && release.expertName && (
                  <div>
                    <p className="font-semibold text-brand-950">About {release.expertName}</p>
                    <p className="mt-1">
                      {release.expertBio ? (
                        release.expertBio
                      ) : release.expertUrl ? (
                        <>
                          <a
                            href={release.expertUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={INLINE_LINK_CLASS}
                          >
                            {release.expertName}
                          </a>{' '}
                          is a {release.expertCredential ?? 'qualified mechanic'} at{' '}
                          {release.expertWorksFor ?? 'Your Local Mechanic'}.
                        </>
                      ) : (
                        buildExpertSentence(release)
                      )}{' '}
                      Quotes attributed to a named expert reflect that individual's professional
                      view and are not legal advice.
                    </p>
                  </div>
                )}

                <div>
                  <p className="font-semibold text-brand-950">Data sources and attribution</p>
                  <p className="mt-1">
                    Where this release cites council or DfT figures, please attribute them as:{' '}
                    <span className="font-medium text-brand-950">{DATA_ATTRIBUTION_LINE}</span>, and
                    link to the relevant{' '}
                    <Link to="/councils" className={INLINE_LINK_CLASS}>
                      council rating page
                    </Link>
                    . {OGL_NOTE}
                    {release.dataSources ? ` ${release.dataSources}` : ''}
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-brand-950">Not legal advice</p>
                  <p className="mt-1">{NOT_LEGAL_ADVICE_LINE}</p>
                </div>
              </div>
            </section>

            <div className="mt-8">
              <MediaContact
                contactName={release.mediaContactName}
                contactEmail={release.mediaContactEmail}
                interviewsNote={release.interviewsNote}
              />
            </div>

            <div className="mt-8">
              <Link
                to="/press"
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                All press releases
              </Link>
            </div>
          </div>
        </section>
      </article>
    </div>
  );
}
